import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { 
  ShareIcon, 
  CalendarIcon, 
  MapPinIcon as LocationMarkerIcon, 
  CurrencyRupeeIcon, 
  ClockIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { motion } from 'framer-motion';

// Animation variants for staggered animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: { duration: 0.5, ease: 'easeOut' }
  }
};

// Skeleton loading component for better UX during data fetch
const EventSkeleton = () => (
  <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse">
    <div className="w-full aspect-video bg-gray-200 rounded-xl mb-6"></div>
    <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
    <div className="grid grid-cols-2 gap-4 mt-8">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="h-12 bg-gray-200 rounded"></div>
      ))}
    </div>
  </div>
);

/**
 * EventDetails component displays detailed information about a single event.
 * It fetches event data from Firestore based on the event ID from the URL,
 * and provides options to register for the event or share it.
 * @param {{ loadingBarRef: React.MutableRefObject }} props - Props containing a ref to the top loading bar.
 */
function EventDetails({ loadingBarRef }) {
  // Hook to get URL parameters, specifically the eventId
  const { eventId } = useParams();
  // Hook for programmatic navigation
  const navigate = useNavigate();
  // State to store the fetched event data
  const [event, setEvent] = useState(null);
  // State to manage the loading status while fetching data
  const [loading, setLoading] = useState(true);
  // State to track if the user has accepted the terms of service
  const [tosAccepted, setTosAccepted] = useState(false);
  // State to manage the text and disabled status of the registration button
  const [buttonState, setButtonState] = useState({
    text: 'Register Now',
    disabled: false
  });

  // useEffect hook to fetch event data when the component mounts or eventId changes.
  useEffect(() => {
    /**
     * Fetches a single event document from the 'events' collection in Firestore.
     */
    const fetchEvent = async () => {
      try {
        // Create a reference to the specific event document
        const eventRef = doc(db, 'events', eventId);
        // Fetch the document snapshot
        const eventSnap = await getDoc(eventRef);
        // If the document exists, update the event state with its data
        if (eventSnap.exists()) {
          setEvent({ id: eventSnap.id, ...eventSnap.data() });
        }
      } catch (error) {
        // Log any errors that occur during the fetch operation
        console.error('Error fetching event:', error);
      } finally {
        // Set loading to false once the fetch operation is complete
        setLoading(false);
      }
    };

    // Call the fetch function
    fetchEvent();
  }, [eventId]); // Dependency array ensures this runs only when eventId changes

  /**
   * Formats a Firestore timestamp into a readable date string.
   * @param {object} timestamp - The Firestore timestamp object.
   * @returns {string} A formatted date string (e.g., "MMMM dd, yyyy").
   */
  const formatDate = (timestamp) => {
    // Return an empty string if the timestamp is invalid
    if (!timestamp) return '';
    // Convert timestamp to Date object
    const date = timestamp.toDate();
    // Format the date using date-fns
    return format(date, 'MMMM dd, yyyy');
  };

  // Check if the event registration is still open by comparing the last date to register with the current date.
  const isEventLive = event?.lastDateToRegister?.toDate() > new Date();

  /**
   * Handles the click event for the "Register Now" button.
   * It shows a loading state and navigates to the event registration page.
   */
  const handleRegisterNow = () => {
    // Update button state to show loading indicator
    setButtonState({ text: 'Loading...', disabled: true });
    
    // Start the top loading bar if the ref is available
    if (loadingBarRef.current) {
      loadingBarRef.current.continuousStart();
    }

    // Simulate a short delay for better user experience before navigating
    setTimeout(() => {
      // Restore button state
      setButtonState({ text: 'Register Now', disabled: false });
      // Complete the loading bar animation
      if (loadingBarRef.current) {
        loadingBarRef.current.complete();
      }
      // Navigate to the registration page for the current event
      navigate(`/${eventId}/register`);
    }, 800);
  };

  /**
   * Handles the sharing of the event using the Web Share API if available,
   * otherwise falls back to copying the URL to the clipboard.
   */
  const handleShare = async () => {
    try {
      // Use Web Share API if supported by the browser
      if (navigator.share) {
        await navigator.share({
          title: event.title,
          text: event.description,
          url: window.location.href
        });
      } else {
        // Fallback for browsers that don't support the Web Share API
        navigator.clipboard.writeText(window.location.href);
        // Optionally, provide feedback to the user that the link has been copied.
      }
    } catch (error) {
      // Log any errors that occur during sharing
      console.error('Error sharing:', error);
    }
  };

  // Display the skeleton loader while event data is being fetched.
  if (loading) return <EventSkeleton />;

  // Display a "not found" message if the event does not exist after loading.
  if (!event) return (
    <motion.div 
      className="text-center mt-20 px-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
    >
      <h2 className="text-2xl font-semibold text-gray-800">Event not found</h2>
      <p className="text-gray-600 mt-2">This event may have been removed or is no longer available.</p>
      <button 
        onClick={() => navigate('/')}
        className="mt-6 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
      >
        Browse Events
      </button>
    </motion.div>
  );

  return (
    <motion.div 
      className="min-h-screen pt-16 pb-32"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section: Displays the main event banner image */}
      <motion.div 
        className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 mb-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div 
          className="relative aspect-video rounded-xl overflow-hidden shadow-lg"
          variants={itemVariants}
        >
          {/* Event Banner Image */}
          <img 
            src={event.bannerImage} 
            alt={event.title} 
            className="w-full h-full object-cover"
            loading="eager" // Eagerly load the main event image
          />
          {/* Overlay to show when registration is closed */}
          {!isEventLive && (
            <div className="absolute top-4 right-4 bg-red-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-md">
              Registration Closed
            </div>
          )}
        </motion.div>

        {/* Event Title and Share Button Section */}
        <motion.div 
          className="mt-6 flex flex-wrap items-start justify-between gap-4"
          variants={itemVariants}
        >
          {/* Event Title */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 flex-1">
            {event.title}
          </h1>
          {/* Share Button */}
          <button 
            onClick={handleShare}
            className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Share event"
          >
            <ShareIcon className="h-6 w-6" />
          </button>
        </motion.div>

        {/* Quick Info Cards Section: Displays key event details like date, venue, price, etc. */}
        <motion.div 
          className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
          variants={itemVariants}
        >
          {[
            { icon: CalendarIcon, label: 'Event Date', value: formatDate(event.eventDate) },
            { icon: LocationMarkerIcon, label: 'Venue', value: event.venue },
            { icon: CurrencyRupeeIcon, label: 'Registration Fee', value: `₹${event.price}` },
            { icon: ClockIcon, label: 'Last Date to Register', value: formatDate(event.lastDateToRegister) }
          ].map((item, index) => (
            <div 
              key={index}
              className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
            >
              {/* Icon and Labels for each info card */}
              <div className="flex items-center space-x-3">
                <item.icon className="h-5 w-5 text-indigo-600" />
                <div>
                  <p className="text-sm text-gray-500">{item.label}</p>
                  <p className="font-medium text-gray-900">{item.value}</p>
                </div>
              </div>
            </div>
          ))}
        </motion.div>

        {/* Event Description Section */}
        <motion.div 
          className="mt-8 bg-white rounded-lg p-6 shadow-sm"
          variants={itemVariants}
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4">About This Event</h2>
          {/* Event description content, supporting multiline text */}
          <p className="text-gray-600 leading-relaxed whitespace-pre-line">
            {event.description}
          </p>
        </motion.div>

        {/* Dynamic Event Sections: Renders additional sections for the event */}
        <motion.div 
          className="mt-8 space-y-6"
          variants={containerVariants}
        >
          {event.eventSections
            .sort((a, b) => a.order - b.order) // Sort sections by order
            .map((section) => (
              <motion.div 
                key={section.sectionId}
                variants={itemVariants}
                className="bg-white rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
              >
                {/* Section Title */}
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {section.title}
                </h3>
                {/* Section Content */}
                <div className="prose prose-indigo max-w-none text-gray-600">
                  <p className="whitespace-pre-line">{section.content}</p>
                </div>
              </motion.div>
            ))}
        </motion.div>
      </motion.div>

      {/* Fixed Registration Actions Section - Sticky to Bottom */}
      {isEventLive && (
        <motion.div 
          className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-lg backdrop-blur-sm bg-white/95"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1, duration: 0.6, ease: 'easeOut' }}
        >
          <div className="max-w-4xl mx-auto px-4 py-4">
            {/* Registration Section Header */}
            <div className="text-center mb-3">
              <h2 className="text-lg font-bold text-gray-900 mb-1">
                🎉 Register for {event.title}
              </h2>
              <p className="text-sm text-gray-600">
                Register for this event for just 
                <span className="font-semibold text-indigo-600 ml-1">₹{event.price}</span>
              </p>
            </div>
            
            {/* Terms and Policies Checkbox */}
            <div className="flex items-start justify-center space-x-2 mb-4">
              <input 
                type="checkbox"
                id="tos-checkbox"
                checked={tosAccepted}
                onChange={() => setTosAccepted(!tosAccepted)}
                className="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mt-0.5 flex-shrink-0"
              />
              <label htmlFor="tos-checkbox" className="text-sm text-gray-600 select-none">
                I agree to the <a href="/terms-of-service" className="text-indigo-600 hover:text-indigo-800 underline decoration-2 decoration-indigo-500/30" target="_blank" rel="noopener noreferrer">Terms of Service</a>, <a href="/privacy-policy" className="text-indigo-600 hover:text-indigo-800 underline decoration-2 decoration-indigo-500/30" target="_blank" rel="noopener noreferrer">Privacy Policy</a>, <a href="/event-rules" className="text-indigo-600 hover:text-indigo-800 underline decoration-2 decoration-indigo-500/30" target="_blank" rel="noopener noreferrer">Event Rules &amp; Regulations</a>, <a href="/payment-policy" className="text-indigo-600 hover:text-indigo-800 underline decoration-2 decoration-indigo-500/30" target="_blank" rel="noopener noreferrer">Payment Policy</a> and <a href="/refund-policy" className="text-indigo-600 hover:text-indigo-800 underline decoration-2 decoration-indigo-500/30" target="_blank" rel="noopener noreferrer">Refund Policy</a>.
              </label>
            </div>
            
            {/* Registration Action Button */}
            <div className="text-center">
              <button
                onClick={handleRegisterNow}
                disabled={!tosAccepted || buttonState.disabled}
                className={`w-full max-w-md px-8 py-4 text-lg font-bold rounded-xl transition-all duration-300 transform active:scale-95 shadow-xl
                  ${!tosAccepted || buttonState.disabled 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-md' 
                    : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 focus:ring-4 focus:ring-indigo-300 hover:shadow-2xl hover:scale-105'
                  }`}
              >
                {buttonState.disabled ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>{buttonState.text}</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <span>🚀</span>
                    <span>{buttonState.text}</span>
                  </div>
                )}
              </button>
            </div>
            
            {/* Quick Registration Info */}
            <div className="mt-3 text-center">
              <p className="text-xs text-gray-500">
                Secure payment • Instant confirmation 
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}

export default EventDetails;
